import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SearchInput from 'search/inputs/SearchInput';
import {
  StandardFilter,
  Operator,
  FilterKey,
  Filter,
  STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY,
  isStandardFilterArrayValue,
} from 'search/SearchConstants';
import {typeToName} from 'util/NameUtil';
import pickDefined from 'util/pickDefined';

import * as FilterUtil from '../FilterUtil';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import FilterFormSkeletonNodeLayout from './base/FilterFormSkeletonNodeLayout';
import {appendUniqueFilterValue} from './base/filterValueUtils';
import OperatorDropdown from './base/OperatorDropdown';
import PrimaryFilterFormSkeleton from './base/PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';

export interface SampleDefinitionFilterFormProps extends BaseFilterFormProps {
  filter: StandardFilter;
  filters: Array<Filter>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: StandardFilter) => void;
  onRemove: () => void;
  operators: Array<Operator>;
  onChangeOperator: (value: Operator) => void;
  selectedOperator: Operator;
}

export default function SampleDefinitionFilterForm(props: SampleDefinitionFilterFormProps): JSX.Element {
  const getCurrentFilterValue = (): Array<string | null> => {
    const value = props.filter.value;

    // TODO (Search): Implement a more generic isValidStandardFilterValue function that takes the whole
    // filter in, and does the validation based on the operator value."
    assert(isStandardFilterArrayValue(value), 'Sample Definition filter value must be an array');

    return value as Array<string | null>;
  };

  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const operatorDropdown: JSX.Element = (
    <OperatorDropdown
      buttonSize={props.buttonSize}
      onChangeOperator={(operator) => {
        const defaultValueFactory = STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY[operator];
        if (!defaultValueFactory) {
          throw new Error(
            `Attempted to select operator ${operator} for standard filter, but no default value factory was found.`
          );
        }
        props.onChangeFilter({...props.filter, operator, value: defaultValueFactory()});
      }}
      operators={props.operators}
      selectedOperator={props.filter.operator}
    />
  );

  // Filter sample definitions on the current study in Study Items
  const studyApiIds: Array<string> = FilterUtil.getFilterValueByKey(
    props.filters,
    FilterKey.IN_STUDY
  ) as Array<string>;

  // TODO (Search): Improve this error handling when we implement cross-filter dependencies
  // This should show a validation warning and omit the filter rather than return a hard error
  assert(studyApiIds.length === 1, 'Expected exactly one study filter');
  const additionalFilters: Array<Filter> = [
    {key: FilterKey.IN_STUDY, operator: Operator.IS, value: studyApiIds},
  ];

  const nodeGrid: Array<Array<JSX.Element>> = [[operatorDropdown]];
  if ([Operator.IS_ONE_OF, Operator.IS_NONE_OF].includes(props.filter.operator)) {
    const searchInput = (
      <SearchInput
        autoFocus={false}
        preserveQueryOnSelect={true}
        isMulti={true}
        additionalFilters={additionalFilters}
        onSelect={(id) => {
          const value = getCurrentFilterValue();
          const newValue = appendUniqueFilterValue(value, id);
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        onRemove={(id) => {
          const value = getCurrentFilterValue();
          const newValue = value.filter((prevId) => prevId !== id);
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        placeholder={`Search for a ${typeToName(ModelTypeConstants.SAMPLE_DEFINITION)}`}
        selected={getCurrentFilterValue()}
        types={[ModelTypeConstants.SAMPLE_DEFINITION]}
      />
    );
    nodeGrid.push([searchInput]);
  }
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        content={<FilterFormSkeletonNodeLayout nodeGrid={nodeGrid} />}
        dropdownToggleText={props.dropdownToggleText}
      />
    );
  } else {
    if (!props.onRemove) {
      throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
    }
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={nodeGrid}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
}
